import { GeneralHash } from "../types";

interface AddSearchParamsToUrlParams {
  url: string;
  queryParams: {
    [param: string]: string;
  };
  overwrite?: boolean;
}

export const addSearchParamsToUrl = ({
  url,
  queryParams,
  overwrite,
}: AddSearchParamsToUrlParams): string => {
  if (!url) return '';
  try {
    const u = new URL(url);
    const urlParamsStr = u.searchParams.toString();
    const existingParamsObj: Record<string, string> = {};
    if (urlParamsStr) {
      const queries = urlParamsStr.split('&');
      const queryKeyValues = queries.map(q => q.split('='));
      queryKeyValues.forEach(([key, value]) => {
        existingParamsObj[key] = value;
      });
    }
    const combinedParamsObj = overwrite ? {
      ...existingParamsObj,
      ...queryParams,
    } : { ...queryParams, ...existingParamsObj };
    combinedParamsObj['referer'] = encodeURIComponent(window.location.href)
    const allParamsStr = Object.keys(combinedParamsObj).map(k => `${k}=${combinedParamsObj[k]}`).join('&');
    const urlWithoutParams = `${u.origin}${u.pathname !== '/' ? u.pathname : ''}`;
    return `${urlWithoutParams}${Object.keys(combinedParamsObj).length > 0 ? `?${allParamsStr}` : ''}`;
  } catch (e) {
    return url;
  }
};

interface IsMailToLinkParams {
  link: string;
}

export const isMailToLink = ({ link }: IsMailToLinkParams): boolean => {
  const re = new RegExp('mailto:');
  return re.test(link);
};

interface ExtractDecodedRegexMatchSafeParams {
  regexMatch: RegExpExecArray | null;
}

export const extractDecodedRegexMatchSafe = ({
  regexMatch,
}: ExtractDecodedRegexMatchSafeParams): string | null => (
  regexMatch && regexMatch.length >= 2 ? decodeURIComponent(regexMatch[1]) : null
);

interface ParseEmailObjectsFromMailtoLinkParams {
  link: string;
}

export const parseEmailObjectsFromMailtoLink = ({
  link,
}: ParseEmailObjectsFromMailtoLinkParams) => {
  const toAddress = /mailto:([^$?]+)/.exec(link);
  const subject = /subject=([^$&]+)/.exec(link);
  const body = /body=([^$&]+)/.exec(link);
  return {
    toAddress: extractDecodedRegexMatchSafe({ regexMatch: toAddress }),
    subject: extractDecodedRegexMatchSafe({ regexMatch: subject }),
    body: extractDecodedRegexMatchSafe({ regexMatch: body }),
  };
};

interface CreateMailtoLinkParams {
  toAddress: string;
  subject: string;
  body: string;
}

export const createMailtoLink = ({ toAddress, subject, body }: CreateMailtoLinkParams): string => (
  `mailto:${toAddress || ''}?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(body || '')}`
);

interface RemoveUrlProtocolParams {
  url?: string;
}

export const removeUrlProtocol = ({ url }: RemoveUrlProtocolParams): string => {
  if (!url) return '';
  return url.replace(/https?:\/\//, '');
};

interface PrependUrlProtocolParams {
  url: string;
}

export const prependUrlProtocol = ({ url }: PrependUrlProtocolParams): string => {
  if (url && !/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
};

interface cleanUrlForMatchingProps {
  url: string;
}

export const cleanUrlForMatching = ({ url }: cleanUrlForMatchingProps): string => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.search = '';
    let cleanUrl = parsedUrl.toString().toLowerCase();
    if (cleanUrl.endsWith('/')) {
      cleanUrl = cleanUrl.slice(0, -1);
    }
    return cleanUrl;
  } catch (err) {
    return url;
  }
};

interface ExtractQueryParamValProps {
  queryParam: string;
  search?: string;
}

export const extractQueryParamVal = ({ queryParam, search = window.location.search }: ExtractQueryParamValProps): string => {
  const paramsObj = createParamsObject({ search });
  return paramsObj[queryParam] || paramsObj[`amp;${queryParam}`] || '';
};

export const createParamsObject = ({search = window.location.search}: {search?: string}): { [k: string]: string; } => {
  const currentParams = new URLSearchParams(search);
  return Object.fromEntries(currentParams.entries());
}

// Parse query params from a full URL (e.g. the embedding page's referer), guarding
// against malformed/relative values. Lets embedded forms read URL parameters from the
// parent page rather than the widget's own (param-less) location.
interface CreateParamsObjectFromUrlProps {
  url?: string;
}

export const createParamsObjectFromUrl = ({ url }: CreateParamsObjectFromUrlProps = {}): { [k: string]: string } => {
  if (!url) return {};
  try {
    return createParamsObject({ search: new URL(url).search });
  } catch {
    return {};
  }
};

export const extractQueryParamArray = ({ queryParam, search = window.location.search }: ExtractQueryParamValProps): string[] => {
  const currentParams = new URLSearchParams(search);
  return currentParams.getAll(queryParam);
}

export const urlRegex = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:[0-9]+)?(\/.*)?$/i;

export const isUrl = (value: string): boolean => urlRegex.test(value);

export const stripUrls = (text: string): string => {
  if (!text) return '';
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, '');
};

const containsWwwRegex = /^(https?:\/\/www\.|www\.)[a-zA-Z0-9.-]+\.[a-z]{2,}\/?$/;
export const validateWwwRegex = (url: string): boolean => url.trim() !== '' && (!containsWwwRegex.test(url));
export const invalidUrlErrorMsg = 'Using a "www" subdomain restricts search results in an unintended way. Please remove "www." before saving.';

const domainOnlyRegex = /^(https?:\/\/)?[a-zA-Z0-9]+([\-\.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
export const validateDomainOnly = (url: string): boolean => url.trim() !== '' && (domainOnlyRegex.test(url));
export const invalidDomainErrorMsg = 'Please enter just the domain (e.g. "example.com" or "sub.example.com") without any page paths if you want to use multiple domains.';

const urlWithPathRegex = /^(https?:\/\/)?[a-zA-Z0-9]+([\-\.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}(\/[^\s?]*)?$/;
export const validateDomainAndPath = (url: string): boolean => url.trim() !== '' && (urlWithPathRegex.test(url));
export const invalidPathErrorMsg = 'Please enter a valid URL without parameters (e.g. "example.com" or "example.com/path").';

const urlWithPathAndParamsRegex = /^(https?:\/\/)?[a-zA-Z0-9]+([\-\.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}(\/[^\s]*)?(\?[a-zA-Z0-9_&=\-\.%]*)?$/;
export const validateDomainAndPathAndParams = (url: string): boolean => url.trim() !== '' && (urlWithPathAndParamsRegex.test(url));
export const invalidPathAndParamsErrorMsg = 'Please enter a valid URL (e.g. "example.com", "example.com/path", or "example.com/path?param=value").';

export const buildUrlParams = (params: GeneralHash) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      searchParams.append(key, JSON.stringify(value));
    } else {
      searchParams.append(key, String(value));
    }
  });

  return `?${searchParams.toString()}`;
}

interface StripPersonalizationIdFromUrlParams {
  url: string;
}

export const stripPersonalizationIdFromUrl = ({ url }: StripPersonalizationIdFromUrlParams): string => {
  if (!url) return '';

  try {
    const isRelative = !url.includes('://') && url.startsWith('/');
    const base = isRelative ? 'https://dummy-base.com' : undefined;
    const parsedUrl = new URL(isRelative ? url : (url.includes('://') ? url : `https://${url}`), base);

    if (parsedUrl.searchParams.has('personalization_id')) {
      parsedUrl.searchParams.delete('personalization_id');
    }

    if (isRelative) {
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    }

    let result = parsedUrl.toString();
    if (!url.includes('://')) {
      result = result.replace(/^https?:\/\//, '');
    }

    return result;
  } catch {
    let result = url.replace(/\?personalization_id=[^&]*&/, '?');
    result = result.replace(/&personalization_id=[^&]*&/, '&');
    result = result.replace(/[?&]personalization_id=[^&]*$/, '');

    return result;
  }
};

interface DecomposeUrlForDisplayParams {
  url: string;
}

export interface DecomposedUrlForDisplay {
  origin: string;
  directories: string;
  identity: string;
  query: string;
}

// Splits on the pathname rather than the raw string: a raw split would put a
// UTM query's last `&`-delimited segment in the protected `identity` slot.
export const decomposeUrlForDisplay = (
  { url }: DecomposeUrlForDisplayParams,
): DecomposedUrlForDisplay | null => {
  try {
    const parsed = new URL(url);
    const keepsTrailingSlash = parsed.pathname.endsWith('/') && parsed.pathname !== '/';
    const segments = parsed.pathname.replace(/\/$/, '').split('/').filter(Boolean);
    const lastSegment = segments.pop() ?? '';

    return {
      origin: parsed.origin,
      directories: segments.length > 0 ? `/${segments.join('/')}/` : '/',
      identity: lastSegment === '' ? '' : `${lastSegment}${keepsTrailingSlash ? '/' : ''}`,
      query: `${parsed.search}${parsed.hash}`,
    };
  } catch {
    return null;
  }
};
